import { type KeyboardEvent, useRef } from "react";

import type { FormControlType } from "types/formType";
import type { IFormInputs } from "types/uiSchema";
import RequiredIndicator from "ui/RequiredIndicator/RequiredIndicator";
import { Checkbox } from "uiShadcn/checkbox";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "uiShadcn/form";

export interface MultiCheckboxProps {
    control: FormControlType;
    uiData: IFormInputs;
}

export default function MultiCheckbox({ control, uiData }: MultiCheckboxProps) {
    const name = uiData["x-json-schema-reference-property"];
    const isRequired = uiData?.required?.includes("true");
    const checkboxRefs = useRef<(HTMLButtonElement | null)[]>([]);

    const dictionary = uiData["x-dictionary"];
    const dictionaryLength = dictionary?.length ?? 0;

    if (!name || !dictionaryLength) return null;

    const handleKeyDown = (index: number, event: KeyboardEvent) => {
        const { key } = event;

        if (key === "ArrowDown" || key === "ArrowRight") {
            event.preventDefault();
            const nextIndex = (index + 1) % dictionaryLength;
            checkboxRefs.current[nextIndex]?.focus();
        } else if (key === "ArrowUp" || key === "ArrowLeft") {
            event.preventDefault();
            const prevIndex = (index - 1 + dictionaryLength) % dictionaryLength;
            checkboxRefs.current[prevIndex]?.focus();
        }
    };

    return (
        <FormField
            control={control}
            name={name}
            render={() => (
                <FormItem className="flex w-full flex-col gap-n2">
                    {uiData?.description && (
                        <FormLabel>
                            {uiData?.description}
                            {isRequired && <RequiredIndicator />}
                        </FormLabel>
                    )}
                    {dictionary?.map(({ key, value }, index) => (
                        <FormField
                            key={key}
                            control={control}
                            name={name}
                            render={({ field }) => {
                                const valuesSet = new Set(field?.value ?? []);
                                const parsedKey = key.toString();

                                const handleCheckedChange = (checked: boolean | "indeterminate") => {
                                    if (checked) valuesSet.add(parsedKey);
                                    else valuesSet.delete(parsedKey);

                                    field.onChange(Array.from(valuesSet));
                                };

                                return (
                                    <FormItem className="flex flex-row items-start gap-n2">
                                        <FormControl>
                                            <Checkbox
                                                ref={(el) => {
                                                    checkboxRefs.current[index] = el;
                                                }}
                                                checked={valuesSet?.has(parsedKey)}
                                                onCheckedChange={handleCheckedChange}
                                                dataTestId={`Checkbox-${name}-${key}`}
                                                onKeyDown={(e) => handleKeyDown(index, e)}
                                                aria-label={value}
                                            />
                                        </FormControl>
                                        <FormLabel>{value}</FormLabel>
                                    </FormItem>
                                );
                            }}
                        />
                    ))}
                    <FormMessage />
                </FormItem>
            )}
        />
    );
}
